/**
 * Pure rules of the check of a pressed tour row (QA doc docs features/tour-search-performance/qa.md "Phase 2": R-P2-4, R-P2-5, R-P2-17). The
 * state applies them; they hold no store, no timer and no request.
 */
import { CalcTourPackageResponse, RefreshPriceResultEnum, TourPackage } from '@appTypes/api.types';
import { TOUR_PRICE_CHANGE_THRESHOLD, TourChecksModel, TourRowCheck, tourRowKey } from './tours-row-checks.types';

/** R-P2-17: do the totals of the page still follow the sort the agent chose? Only a price sort is judged; the default order is by price. */
export const priceOrderBroken = (
  packages: readonly TourPackage[],
  sortByField: string | null | undefined,
  sortOrder: string | null | undefined
): boolean => {
  if (sortByField && sortByField !== 'TotalPrice') return false;
  const desc = sortOrder?.toUpperCase() === 'DESC';
  for (let i = 1; i < packages.length; i++) {
    const before = packages[i - 1].totalPrice ?? 0;
    const after = packages[i].totalPrice ?? 0;
    if (desc ? after > before : after < before) return true;
  }
  return false;
};

/**
 * The outcome of the first Details answer of a row, in the row's terms: another refresh result than Success (or no package) = no longer
 * available; a total that moved by more than the dialog's threshold = changed (the Details package, keyed as the row, takes the row's place);
 * else confirmed.
 */
export const rowCheckOfDetails = (row: TourPackage, answer: CalcTourPackageResponse): TourRowCheck => {
  const checked = answer.package;
  if ((answer.refreshPriceResult && answer.refreshPriceResult !== RefreshPriceResultEnum.Success) || !checked)
    return { state: 'unavailable', errorCode: 'OFFER_NO_LONGER_AVAILABLE', traceId: answer.activityId ?? null };
  const before = row.totalPrice ?? 0;
  const after = checked.totalPrice ?? 0;
  if (!after || Math.abs(before - after) <= TOUR_PRICE_CHANGE_THRESHOLD) return { state: 'confirmed' };
  return {
    state: 'changed',
    previousTotalPrice: before,
    package: { ...checked, id: row.id, cacheKey: row.cacheKey }
  };
};

/** A Details answer that could not be had (an error answer, a failed call): the row is not checked, its supplier named when known. */
export const rowCheckFailed = (
  row: TourPackage,
  error: { errorCode?: string | null; activityId?: string | null } | null | undefined
): TourRowCheck => ({
  state: 'failed',
  errorCode: error?.errorCode ?? 'SUPPLIER_UNAVAILABLE',
  supplier: row.hotel?.supplierCode ?? null,
  traceId: error?.activityId ?? null
});

/** The shown rows with the checked package of every `changed` row in their place (R-P2-4: every other row keeps its identity). */
export const withCheckedPackages = <T extends TourPackage>(packages: T[], checks: TourChecksModel | undefined): T[] => {
  if (!checks || !Object.values(checks.rows).some((r) => r.package)) return packages;
  return packages.map((p) => (checks.rows[tourRowKey(p)]?.package as T | undefined) ?? p);
};

/** The checks of the page with one row's new check; `resort` follows a changed total that broke a price sort (R-P2-17). */
export const withRowCheck = (
  current: TourChecksModel | undefined,
  key: string,
  check: TourRowCheck,
  packages: readonly TourPackage[],
  sort: { sortByField?: string | null; sortOrder?: string | null }
): TourChecksModel => {
  const checks: TourChecksModel = {
    rows: { ...(current?.rows ?? {}), [key]: check },
    resort: current?.resort ?? false
  };
  if (check.state === 'changed' && !checks.resort)
    checks.resort = priceOrderBroken(withCheckedPackages([...packages], checks), sort.sortByField, sort.sortOrder);
  return checks;
};
